import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import type { DocsLangCode } from '@gitcenter/shared';
import { getDocMarkdown } from './registry';
import { setDocsLangCookie } from './docsLang';
import { DocNotFound } from './DocNotFound';
import './DocPage.css';

interface DocPageProps {
    lang: DocsLangCode;
}

export function DocPage({ lang }: DocPageProps) {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const md = slug ? getDocMarkdown(lang, slug) : null;

    const switchLang = useCallback(
        (next: DocsLangCode) => {
            if (!slug || next === lang) return;
            setDocsLangCookie(next);
            const hash = typeof window !== 'undefined' ? window.location.hash : '';
            navigate(`/docs/${next}/${slug}${hash}`);
        },
        [slug, lang, navigate]
    );

    if (!slug || md === null) {
        return <DocNotFound />;
    }

    return (
        <article className="doc-page">
            <div className="doc-toolbar">
                <span className="doc-toolbar-label">语言</span>
                <button
                    type="button"
                    className={`doc-lang-btn${lang === 'zh' ? ' doc-lang-btn--active' : ''}`}
                    aria-current={lang === 'zh' ? 'true' : undefined}
                    onClick={() => switchLang('zh')}
                >
                    中文
                </button>
                <button
                    type="button"
                    className={`doc-lang-btn${lang === 'en' ? ' doc-lang-btn--active' : ''}`}
                    aria-current={lang === 'en' ? 'true' : undefined}
                    onClick={() => switchLang('en')}
                >
                    English
                </button>
            </div>
            <div className="doc-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                    {md}
                </ReactMarkdown>
            </div>
        </article>
    );
}
